import { trigger, transition, style, animate } from '@angular/animations';
import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, ViewChild, inject } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, ValidatorFn, Validators } from '@angular/forms';
import { SharedService } from '@shared/services/shared.service';
import { IIHCAdministratorApplication } from 'app/models/licenses/in-home-care/ihc-administrator-application';
import { ProfessionalExperienceComponent } from '../professional-experience/professional-experience.component';
import { ManagementExperienceComponent } from '../management-experience/management-experience.component';
import { AdministratorApplicationService } from 'app/services/license/in-home-care/administrator-application.service';
import { AddressComponent } from 'app/components/reusable/address/address.component';
import { DocumentComponent } from 'app/components/reusable/document/document.component';
import { IStatus } from 'app/models/licenses/status';
import { ILicenseChangeDialog } from '../in-home-care-change/in-home-care-change-dialog.component';

@Component({
  selector: 'administrator-application',
  templateUrl: './administrator-application.component.html',
  styleUrls: ['./administrator-application.component.scss'],
  animations: [
    trigger('slideRight', [
      transition(':enter', [
        style({ transform: 'translateX(100%)' }),
        animate('200ms ease-in', style({ transform: 'translateX(0%)' }))
      ]),
      transition(':leave', [
        animate('200ms ease-in', style({ transform: 'translateX(100%)' }))
      ])
    ])
  ]
})

export class AdministratorApplicationComponent implements OnInit, OnChanges, OnDestroy {
  @ViewChild(DocumentComponent) documents: DocumentComponent;
  @ViewChild(AddressComponent) addresses: AddressComponent;
  @ViewChild(ProfessionalExperienceComponent) professionalExperience: ProfessionalExperienceComponent;
  @ViewChild(ManagementExperienceComponent) managementExperience: ManagementExperienceComponent;
  @Input() entityId: number = 0;
  @Input() entityType: string = '';
  @Input() entityGuid: string = '';
  @Input() isChange: boolean = false;
  @Input() statuses: IStatus[] = [];
  @Input() isEditable: boolean = false;

  public needApplication: boolean = true;
  public isOutOfState: boolean = false;
  public viewingDuplicates: boolean = false;
  public viewingProfessionalExperience: boolean = false;
  public viewingManagementExperience: boolean = false;
  public administratorApplicationTypes: string[] =
  ['Proof of Credential', 'Background Check Request',
   'High School Diploma or Equivalent', 'Resume', 'No Client Contact Plan'];

  public application: IIHCAdministratorApplication = {
    id: 0,
    entityId: 0,
    firstName: '',
    lastName: '',
    middleInitial: '',
    applicationDate: '',
    phone: '',
    email: '',
    contactWithClient: null,
    possibleDuplicate: false,
    highSchool: '',
    highSchoolAddress: '',
    highSchoolFrom: '',
    highSchoolTo: '',
    graduatedHighSchool: false,
    highSchoolDegree: '',
    college: '',
    collegeAddress: '',
    collegeFrom: '',
    collegeTo: '',
    graduatedCollege: false,
    collegeDegree: '',
    other: '',
    otherAddress: '',
    otherFrom: '',
    otherTo: '',
    graduatedOther: false,
    otherDegree: '',
    hasManagementExperience: false,
    hasProfessionalExperience: false,
    signature: '',
    signatureDate: ''
  }

  public applicationForm = new FormGroup({
    firstName: new FormControl('', Validators.required),
    lastName:new FormControl('', Validators.required),
    middleInitial: new FormControl(''),
    phone: new FormControl('' , [Validators.required, Validators.pattern(/^\(?([0-9]{3})\)?[-]?([0-9]{3})[-]?([0-9]{4}).*$/)]),
    email: new FormControl('', [Validators.required, Validators.email]),
    contactWithClient: new FormControl(null),
    highSchool: new FormControl(''),
    highSchoolAddress: new FormControl(''),
    highSchoolFrom: new FormControl<Date>(null),
    highSchoolTo: new FormControl<Date>(null),
    graduatedHighSchool: new FormControl(false),
    highSchoolDegree: new FormControl(''),
    college: new FormControl(''),
    collegeAddress: new FormControl(''),
    collegeFrom: new FormControl<Date>(null),
    collegeTo: new FormControl<Date>(null),
    graduatedCollege: new FormControl(false),
    collegeDegree: new FormControl(''),
    other: new FormControl(''),
    otherAddress: new FormControl(''),
    otherFrom: new FormControl<Date>(null),
    otherTo: new FormControl<Date>(null),
    graduatedOther: new FormControl(false),
    otherDegree: new FormControl(''),
    hasManagementExperience: new FormControl(false, Validators.required),
    hasProfessionalExperience: new FormControl(false, Validators.required),
    signature: new FormControl(''),
    signatureDate: new FormControl('')
  }, {
    validators: [
      this.highSchoolToValidator(),
      this.collegeToValidator(),
      this.otherToValidator()
    ]
  });

  public duplicateAdmins: IIHCAdministratorApplication[] = [];

  public sharedService = inject(SharedService);
  public applicationService = inject(AdministratorApplicationService);

  ngOnInit(): void {
    this.getApplication();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if(changes.isEditable){
      if(this.isEditable){
        this.applicationForm.get('contactWithClient').enable();
        this.applicationForm.get('highSchool').enable();
        this.applicationForm.get('graduatedHighSchool').enable();
        this.applicationForm.get('college').enable();
        this.applicationForm.get('graduatedCollege').enable();
        this.applicationForm.get('other').enable();
        this.applicationForm.get('graduatedOther').enable();
        this.applicationForm.get('hasManagementExperience').enable();
        this.applicationForm.get('hasProfessionalExperience').enable();
      }
      else{
        this.applicationForm.get('contactWithClient').disable();
        this.applicationForm.get('highSchool').disable();
        this.applicationForm.get('graduatedHighSchool').disable();
        this.applicationForm.get('college').disable();
        this.applicationForm.get('graduatedCollege').disable();
        this.applicationForm.get('other').disable();
        this.applicationForm.get('graduatedOther').disable();
        this.applicationForm.get('hasManagementExperience').disable();
        this.applicationForm.get('hasProfessionalExperience').disable();
      }
    }
  }

  ngOnDestroy(): void {
    if(!this.locked || this.isChange){
      this.saveApplication();
    }
  }

  getApplication(): void {
    this.applicationService.getAdministratorApplication(this.entityId, this.entityType).subscribe({
      next: response => this.application = response,
      error: e => console.log('error getting administrator application', e),
      complete: () => {
        this.updateForm();
        if(this.locked && this.entityType === this.sharedService.inHomeCareLicense){
          this.applicationForm.disable;
        }
        if(this.application.id === 0){
          this.needApplication = true;
        }
        else{
          this.needApplication = false;
        }
      }
    });
  }

  updateForm(): void {
    this.applicationForm.reset({
      firstName: this.application.firstName,
      lastName: this.application.lastName,
      middleInitial: this.application.middleInitial,
      phone: this.application.phone,
      email: this.application.email,
      contactWithClient: this.application.contactWithClient,
      highSchool: this.application.highSchool,
      highSchoolAddress: this.application.highSchoolAddress,
      graduatedHighSchool: this.application.graduatedHighSchool,
      highSchoolDegree: this.application.highSchoolDegree,
      college: this.application.college,
      collegeAddress: this.application.collegeAddress,
      graduatedCollege: this.application.graduatedCollege,
      collegeDegree: this.application.collegeDegree,
      other: this.application.other,
      otherAddress: this.application.otherAddress,
      graduatedOther: this.application.graduatedOther,
      otherDegree: this.application.otherDegree,
      hasManagementExperience: this.application.hasManagementExperience,
      hasProfessionalExperience: this.application.hasProfessionalExperience,
      signature: this.application.signature,
      signatureDate: this.application.signatureDate,
    });

    const dateFields = ['highSchoolFrom', 'highSchoolTo', 'collegeFrom', 'collegeTo', 'otherFrom', 'otherTo'];
    dateFields.forEach(field => {
      if (this.application[field]) {
        this.applicationForm.get(field).setValue(new Date(this.application[field]));
      }
    });

    if(this.locked && !this.isChange){
      this.applicationForm.get('contactWithClient').disable();
      this.applicationForm.get('highSchool').disable();
      this.applicationForm.get('graduatedHighSchool').disable();
      this.applicationForm.get('college').disable();
      this.applicationForm.get('graduatedCollege').disable();
      this.applicationForm.get('other').disable();
      this.applicationForm.get('graduatedOther').disable();
      this.applicationForm.get('hasManagementExperience').disable();
      this.applicationForm.get('hasProfessionalExperience').disable();
    }
  }

  updateApplication(): void {
    let form = this.applicationForm.getRawValue();
    this.application.firstName = form.firstName;
    this.application.lastName = form.lastName;
    this.application.middleInitial = form.middleInitial;
    this.application.phone = form.phone;
    this.application.email = form.email;
    this.application.contactWithClient = form.contactWithClient;
    this.application.highSchool = form.highSchool;
    this.application.highSchoolAddress = form.highSchoolAddress;
    this.application.highSchoolFrom = form.highSchoolFrom?.toDateString();
    this.application.highSchoolTo = form.highSchoolTo?.toDateString();
    this.application.graduatedHighSchool = form.graduatedHighSchool;
    this.application.highSchoolDegree = form.highSchoolDegree;
    this.application.college = form.college;
    this.application.collegeAddress = form.collegeAddress;
    this.application.collegeFrom = form.collegeFrom?.toDateString();
    this.application.collegeTo = form.collegeTo?.toDateString();
    this.application.graduatedCollege = form.graduatedCollege;
    this.application.collegeDegree = form.collegeDegree;
    this.application.other = form.other;
    this.application.otherAddress = form.otherAddress;
    this.application.otherFrom = form.otherFrom?.toDateString();
    this.application.otherTo = form.otherTo?.toDateString();
    this.application.graduatedOther = form.graduatedOther;
    this.application.otherDegree = form.otherDegree;
    this.application.hasManagementExperience = form.hasManagementExperience;
    this.application.hasProfessionalExperience = form.hasProfessionalExperience;
    this.application.signature = form.signature;
    this.application.signatureDate = form.signatureDate;
  }

  saveApplication(): void {
    this.updateApplication();
    this.applicationService.updateAdministratorApplication(this.application).subscribe({
      error: e => {
        this.sharedService.toastr.error("Could Not Save Application");
        console.log("error updating administrator application", e);
      }
    });
  }

  createApplication(): void {
    this.applicationService.createAdministratorApplication(this.entityId, this.entityType).subscribe({
      next: response => this.application = response,
      error: e => {
        this.sharedService.toastr.error("Could Not Add Application Information");
        console.log('error creating administrator application', e);
      },
      complete: () => {
        this.needApplication = false;
        this.updateForm();
      }
    });
  }

  requestChange(){
    let dialogData: ILicenseChangeDialog = {
      licenseType: this.sharedService.inHomeCareLicense,
      licenseId: this.entityId,
      licenseGuid: this.entityGuid,
      entityId: 0,
      entityType: this.sharedService.administratorApplication,
      originalValue: '',
      newValue: '',
      changeAction: this.sharedService.change
    }

    this.sharedService.requestChange(dialogData);
  }

  getDuplicateAdmins(): void {
    this.applicationService.getDuplicateAdmins(this.application.firstName, this.application.lastName).subscribe({
      next: response => this.duplicateAdmins = response,
      error: e => console.log('error getting duplicate admins', e),
      complete : () => this.viewingDuplicates = true
    });
  }

  closeDuplicates(): void {
    this.viewingDuplicates = false;
  }

  markNotADuplicate(): void {
    this.sharedService.openConfirm('Mark this admin as not a duplicate?');
    this.sharedService.confirmed().subscribe(
      confirmed => {
        if(confirmed){
          this.application.possibleDuplicate = false;
          this.applicationService.updateAdministratorApplication(this.application).subscribe({
            error: e => {
              this.sharedService.toastr.error("Could Not Update Application");
              console.log('error updating administrator application', e);
            },
            complete: () => {
              this.viewingDuplicates = false;
              this.duplicateAdmins = [];
            }
          });
        }
      }
    );
  }

  managementExperienceSelected(viewing: boolean):void {
    this.viewingManagementExperience = viewing;
  }

  professionalExperienceSelected(viewing: boolean):void {
    this.viewingProfessionalExperience = viewing;
  }

  downloadReferenceDocument(documentType: string): void {
    this.documents.getReferenceDocument(documentType);
  }

  updateOutOfState(isOutOfState: boolean): void {
    this.isOutOfState = isOutOfState;
  }

  public get locked(): boolean {
    return !this.statuses.some(s => s.label === this.sharedService.started) && !this.isEditable;
  }

  public get approved(): boolean {
    return this.statuses.some(s => s.label === this.sharedService.approved);
  }

  //Custom Validators
  highSchoolToValidator(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      const fromDate = control.value.highSchoolFrom;
      const toDate = control.value.highSchoolTo;

      if(fromDate && toDate && toDate < fromDate) {
        return { 'HSdateRangeInvalid': true };
      }
      return null;
    }
  }

  validateHighSchoolTo(): boolean {
    if (this.applicationForm.hasError('HSdateRangeInvalid') && this.applicationForm.get('highSchoolTo').touched) {
      this.applicationForm.get('highSchoolTo').setErrors(['invalid']);
      return true;
    }
    return false;
  }

  collegeToValidator(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      const fromDate = control.value.collegeFrom;
      const toDate = control.value.collegeTo;
      if(fromDate && toDate && toDate < fromDate) {
        return { 'CollegeDateRangeInvalid': true };
      }
      return null;
    }
  }

  validateCollegeTo(): boolean {
    if (this.applicationForm.hasError('CollegeDateRangeInvalid') && this.applicationForm.get('collegeTo').touched) {
      this.applicationForm.get('collegeTo').setErrors(['invalid']);
      return true;
    }
    return false;
  }

  otherToValidator(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      const fromDate = control.value.otherFrom;
      const toDate = control.value.otherTo;
      if(fromDate && toDate && toDate < fromDate) {
        return { 'OtherDateRangeInvalid': true };
      }
      return null;
    }
  }

  validateOtherTo(): boolean {
    if (this.applicationForm.hasError('OtherDateRangeInvalid') && this.applicationForm.get('otherTo').touched) {
      this.applicationForm.get('otherTo').setErrors(['invalid']);
      return true;
    }
    return false;
  }
}
